import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'utils/i18n';
import { BesperPlatformSlatMark, isBesperVendorProviderId } from 'besper-product-marks';
import { AuthenticatedDatasetImage } from './AuthenticatedDatasetImage';
import { ProductSVGIcon } from './ProductSVGIcon';
import { ProductInfoModal } from './ProductInfoModal';
import marketplaceTranslations from './translations';

/**
 * Marketplace product card component - Palantir-style design
 * Clean, professional layout with SVG icons and minimal visual noise
 */
export const MarketplaceCard = ({ 
  product, 
  isSelected, 
  onSelect,
  loading = false,
  disabled = false,
  onOpenModal
}) => {
  const [showInfoModal, setShowInfoModal] = useState(false);
  const openModal = onOpenModal ? () => onOpenModal(product) : () => setShowInfoModal(true);
  const closeModal = onOpenModal ? () => {} : () => setShowInfoModal(false);
  const showModal = onOpenModal ? false : showInfoModal;
  const { language } = useTranslation();
  const t = (key, defaultValue = '') => {
    const keys = key.split('.');
    let value = marketplaceTranslations[language] || marketplaceTranslations.en;
    for (const k of keys) {
      value = value?.[k];
    }
    return value || defaultValue || key;
  };
  const marketplace = product.marketplace || {};
  const stripeMeta = product.metadata || {};
  const providerIdForMark =
    marketplace.providerId || marketplace.provider?.id || stripeMeta.provider;
  const category = marketplace.category || { id: 'other', displayName: 'Other' };
  const categoryDisplayName = category.displayName || t('marketplaceCard.other', 'Other');
  const productName = product.name || t('marketplaceCard.unnamedProduct', 'Unnamed Product');
  const isDatasetProduct = String(stripeMeta.resource_type || '').toLowerCase() === 'dataset';
  const shouldRenderProductLogo =
    !isDatasetProduct && Boolean(marketplace.productLogoUrl || product.metadata?.product_logo_url);
  
  // Get provider display name with proper fallbacks
  const providerDisplayName = marketplace.providerDisplayName || 
                              marketplace.provider?.displayName || 
                              marketplace.providerId || 
                              t('marketplaceCard.unknownProvider', 'Unknown Provider');
  
  // Extract tags from metadata (handle both array and comma-separated string)
  const tags = Array.isArray(marketplace.tags) 
    ? marketplace.tags 
    : (typeof marketplace.tags === 'string' && marketplace.tags.trim() 
        ? marketplace.tags.split(',').map(t => t.trim()).filter(Boolean)
        : []);
  const displayTags = tags.slice(0, 3); // Show first 3 tags
  
  // Upcoming catalog items are non-clickable in production only (see catalog release_status).
  const rawEnv = (
    window._env_?.enviroment ||
    window._env_?.ENVIRONMENT ||
    window._env_?.environment ||
    process.env.REACT_APP_ENVIRONMENT ||
    ''
  )
    .toString()
    .trim()
    .toLowerCase();
  const isProd = rawEnv === 'prod' || rawEnv === 'production';
  const releaseRaw =
    product.metadata?.release_status || marketplace.release_status || product.release_status;
  const releaseNorm =
    typeof releaseRaw === 'string' ? releaseRaw.trim().toLowerCase() : '';
  const isUpcoming = releaseNorm === 'upcoming';
  const isUpcomingDisabled = isUpcoming && isProd;
  
  // Product type label: prefer Stripe/catalog metadata (product_type, vendor_type, partner_only)
  const getProductTypeLabel = () => {
    const md = product.metadata || {};
    const name = (productName || '').toLowerCase();
    const id = (product.id || '').toLowerCase();
    const vendorType = (md.vendor_type || marketplace.vendorType || '').toLowerCase();
    const productType = (md.product_type || marketplace.productType || '').toLowerCase();
    const partnerOnly = md.partner_only === 'true';

    if (partnerOnly || name.includes('resale') || id.includes('resale')) {
      return t('marketplaceCard.productType.partner', 'Partner Product');
    }
    if (vendorType === 'third_party') {
      return t('marketplaceCard.productType.thirdParty', 'Third party product');
    }
    if (productType === 'implementation') {
      return t('marketplaceCard.productType.implementation', 'Implementation product');
    }
    if (productType === 'storage') {
      return t('marketplaceCard.productType.native', 'B-esper product');
    }
    if (productType === 'application') {
      if (vendorType === 'third_party') {
        return t('marketplaceCard.productType.thirdParty', 'Third party product');
      }
      return t('marketplaceCard.productType.native', 'B-esper product');
    }
    if (vendorType === 'besper') {
      return t('marketplaceCard.productType.native', 'B-esper product');
    }

    const categoryId = category.id || 'other';
    const categoryMap = {
      ai_chatbot: t('marketplaceCard.productType.implementation', 'Implementation product'),
      dataset: t('marketplaceCard.productType.native', 'B-esper product'),
      ai_lab: t('marketplaceCard.productType.native', 'B-esper product'),
      erp: t('marketplaceCard.productType.application', 'Application product'),
      crm: t('marketplaceCard.productType.application', 'Application product'),
      productivity: t('marketplaceCard.productType.application', 'Application product'),
      analytics: t('marketplaceCard.productType.application', 'Application product'),
      integration: t('marketplaceCard.productType.implementation', 'Implementation product'),
      developer_tools: t('marketplaceCard.productType.implementation', 'Implementation product'),
      other: t('marketplaceCard.productType.thirdParty', 'Third party product'),
    };
    return categoryMap[categoryId] || t('marketplaceCard.productType.thirdParty', 'Third party product');
  };
  
  const handleClick = () => {
    if (!loading && !disabled && !isUpcomingDisabled) {
      openModal();
    }
  };
  
  // Combine disabled states
  const isFullyDisabled = disabled || isUpcomingDisabled;
  
  return (
    <div
      className={`marketplace-card palantir-style left-aligned ${isSelected ? 'selected' : ''} ${loading ? 'loading' : ''} ${isFullyDisabled ? 'disabled' : ''} ${isUpcomingDisabled ? 'upcoming-disabled' : ''}`}
      onClick={handleClick}
      style={isUpcomingDisabled ? { 
        opacity: 0.5, 
        cursor: 'not-allowed',
        filter: 'grayscale(0.6)'
      } : {}}
      title={disabled ? t('marketplaceCard.loginRequired', 'Please log in to select products') : (isUpcomingDisabled ? t('marketplaceCard.comingSoon', 'This product is coming soon') : '')}
    >
      {/* Top band: product icon + type label (left), provider logo (right) - title row below has full width */}
      <div className="marketplace-card-top-band">
        <div className="product-icon-top-left">
          {shouldRenderProductLogo ? (
            <div className="product-logo-container-left">
              <AuthenticatedDatasetImage
                logoUrl={marketplace.productLogoUrl || product.metadata.product_logo_url}
                alt={productName}
                className="product-logo-img-left"
              />
            </div>
          ) : (
            <ProductSVGIcon
              productName={productName}
              productId={product.id}
              productType={stripeMeta.resource_type}
              productKey={stripeMeta.product_key}
              datasetDestination={marketplace.defaultDatasetDestination || stripeMeta.default_dataset_destination}
              size={48}
              className="product-icon-left"
            />
          )}
          <div className="product-type-label-below-icon">
            {getProductTypeLabel().toUpperCase()}
          </div>
        </div>
        <div className="product-provider-logo-top-right">
          {isBesperVendorProviderId(providerIdForMark) ? (
            <BesperPlatformSlatMark
              size={28}
              className="provider-logo-img besper-vendor-platform-mark"
              title={providerDisplayName}
            />
          ) : (marketplace.providerLogoUrl || marketplace.provider?.logo_url) ? (
            <AuthenticatedDatasetImage
              logoUrl={marketplace.providerLogoUrl || marketplace.provider?.logo_url}
              alt={providerDisplayName}
              className="provider-logo-img"
            />
          ) : (
            <div className="besper-logo-placeholder">
              <span className="besper-logo-letter">B</span>
            </div>
          )}
        </div>
      </div>

      {/* Content Wrapper - Title has full width below top band */}
      <div className="product-card-content-wrapper">
        <div className="product-card-top-content">
          <div className="product-header-palantir-left">
            <h3 className="product-name-palantir-left product-name-with-logo" title={productName}>
              {productName}
            </h3>
          </div>
          
          {/* Metadata Row - Category | Provider */}
          <div className="product-metadata-row">
            <span className="metadata-item">
              <span className="metadata-label">{t('marketplaceCard.category', 'Category')}:</span>
              <span className="metadata-value">{categoryDisplayName}</span>
            </span>
            <span className="metadata-separator">|</span>
            <span className="metadata-item">
              <span className="metadata-label">{t('marketplaceCard.provider', 'Provider')}:</span>
              <span className="metadata-value">{providerDisplayName}</span>
            </span>
          </div>
          
          {/* Product Description - Left Aligned, 2-3 lines */}
          <div className="product-description-container-palantir-left">
            <p className="product-description-palantir-left">
              {marketplace.shortDescription || product.description || t('marketplaceCard.noDescription', 'No description available.')}
            </p>
          </div>
        </div>
        
        {/* Tags - Pushed to Bottom (modal opens on card click) */}
        {displayTags.length > 0 && (
          <div className="product-card-bottom-row">
            <div className="product-tags-container product-card-tags-bottom">
              {displayTags.map((tag, index) => (
                <span key={index} className="product-tag-pill">
                  {tag}
                </span>
              ))}
              {tags.length > 3 && (
                <span className="product-tag-more">+{tags.length - 3}</span>
              )}
            </div>
          </div>
        )}
      </div>
      {!onOpenModal && showModal && createPortal(
        <ProductInfoModal
          product={product}
          onClose={closeModal}
          onSelect={onSelect}
        />,
        document.body
      )}
    </div>
  );
};

